// Étape « Questionnaire » de la mesure zéro — FACULTATIVE.
//
// Deux usages, côte à côte :
//   · pendant l'entretien, le chercheur note les réponses (dictée possible) ;
//   · il envoie au client un LIEN PERSONNEL : le client répond sans compte, et
//     ses réponses reviennent ici, séparées de celles de l'entretien.
//
// Il n'y a pas d'envoi d'e-mail côté serveur : « Envoyer par e-mail » ouvre la
// messagerie de l'utilisateur, message pré-rempli. Le lien n'est visible qu'au
// moment où il est créé (le serveur n'en garde que l'empreinte) ; pour le
// renvoyer plus tard, on en génère un nouveau, ce qui invalide l'ancien.
import { useEffect, useState, type FormEvent } from 'react';
import {
  useCreateInvitation,
  useInternalAnswers,
  useInvitations,
  useMesure0Questions,
  useRevokeInvitation,
  useRotateInvitation,
  useSaveInternalAnswers,
} from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { InvitationStatus, Mesure0Answers, Mesure0Invitation } from '../api/types';
import { Button, buttonClass, ErrorBanner, Field, Spinner } from '../components/ui';
import { hrefFor } from '../router';
import { TextArea } from '../write/Forms';

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

const STATUS_LABELS: Record<InvitationStatus, string> = {
  pending: 'En attente',
  answered: 'A répondu',
  expired: 'Lien expiré',
  revoked: 'Lien coupé',
};

export function publicLink(token: string): string {
  return `${window.location.origin}${hrefFor(`/mesure0/r/${token}`)}`;
}

function mailtoFor(email: string | null, name: string | null, link: string, projectName: string): string {
  const subject = `Questionnaire — état des lieux de ${projectName}`;
  const body = [
    `Bonjour${name ? ` ${name}` : ''},`,
    '',
    `Je prépare une étude sur ${projectName}. Pour mieux cadrer le besoin initial, pourriez-vous répondre à ce court questionnaire (une dizaine de questions, environ 15 minutes) ?`,
    '',
    link,
    '',
    'Rien n’est obligatoire : vos réponses sont indicatives, répondez à ce que vous savez.',
    'Aucun compte n’est nécessaire, et vous pouvez revenir compléter vos réponses tant que le lien est valide (30 jours).',
    '',
    'Merci !',
  ].join('\n');
  return `mailto:${encodeURIComponent(email ?? '')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function Mesure0Questionnaire({ projectId, projectName }: { projectId: string; projectName: string }) {
  const questions = useMesure0Questions();
  const saved = useInternalAnswers(projectId);
  const save = useSaveInternalAnswers(projectId);
  const [answers, setAnswers] = useState<Mesure0Answers>({});
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState(false);

  // On n'initialise qu'une fois : une relecture du cache ne doit pas écraser ce
  // que l'on est en train de taper.
  useEffect(() => {
    if (saved.data && !dirty) setAnswers(saved.data);
  }, [saved.data, dirty]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await save.mutateAsync(answers);
      setDirty(false);
    } catch {
      /* rendu par le bandeau */
    }
  }

  if (questions.isPending || saved.isPending) return <Spinner label="Chargement du questionnaire…" />;
  if (questions.isError) return <ErrorBanner>{errorMessage(questions.error)}</ErrorBanner>;

  const answered = Object.values(answers).filter((v) => v.trim()).length;

  return (
    <div className="m0-quest">
      <p className="m0-note">
        Facultatif. Envoyez-le au client pour qu’il y réponde lui-même, ou notez ses réponses pendant
        l’entretien. Chaque répondant garde ses propres réponses : leurs écarts sont déjà un signal.
      </p>

      <SendToClient projectId={projectId} projectName={projectName} />

      {/* Les dix questions sont REPLIÉES par défaut : dépliées, elles repoussaient
          le bouton « Continuer » hors de vue, alors que cette étape est
          facultative et souvent passée. */}
      <section className="m0-interview">
        <button
          type="button"
          className="m0-interview-toggle"
          aria-expanded={open}
          aria-controls="m0-interview-form"
          onClick={() => setOpen((o) => !o)}
        >
          <span className="m0-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
          <span>Noter les réponses pendant l’entretien</span>
          <span className="m0-interview-count">{answered}/{questions.data.length}</span>
        </button>

      {open ? (
      <form id="m0-interview-form" className="write-form m0-questions" onSubmit={submit} noValidate>
        {questions.data.map((q, i) => (
          <TextArea
            key={q.key}
            label={`${i === 0 ? '' : `${i}. `}${q.label}`}
            value={answers[q.key] ?? ''}
            rows={2}
            onChange={(v) => {
              setAnswers((a) => ({ ...a, [q.key]: v }));
              setDirty(true);
            }}
          />
        ))}
        {save.error ? <ErrorBanner>{errorMessage(save.error)}</ErrorBanner> : null}
        <Button type="submit" variant="ghost" loading={save.isPending} disabled={!dirty}>
          {dirty ? 'Enregistrer les réponses' : 'Réponses enregistrées'}
        </Button>
      </form>
      ) : dirty ? (
        <p className="zy-texte-muet">Des réponses ne sont pas encore enregistrées : dépliez pour les enregistrer.</p>
      ) : null}
      </section>
    </div>
  );
}

// --- Envoi au client ---------------------------------------------------------

function SendToClient({ projectId, projectName }: { projectId: string; projectName: string }) {
  const invitations = useInvitations(projectId);
  const create = useCreateInvitation(projectId);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // Le lien fraîchement créé (ou régénéré), seul moment où il est lisible.
  const [fresh, setFresh] = useState<{ id: string; link: string; name: string | null; email: string | null } | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const inv = await create.mutateAsync({
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
      });
      setFresh({ id: inv.id, link: publicLink(inv.token), name: name.trim() || null, email: email.trim() || null });
      setName('');
      setEmail('');
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <section className="m0-send" aria-labelledby="m0-send-title">
      <p className="m0-send-title" id="m0-send-title">
        Envoyer le questionnaire au client <span className="m0-optional">(facultatif)</span>
      </p>
      <form className="m0-send-form" onSubmit={submit} noValidate>
        <div className="m0-send-fields">
          <Field label="Nom" value={name} onChange={(e) => setName(e.target.value)} placeholder="Claire Martin, PO" />
          <Field
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="claire@client.fr"
          />
        </div>
        {/* Le bouton vit DANS le cadre, sur sa propre ligne : en trois colonnes,
            il débordait du panneau latéral. Il crée le lien personnel, puis
            propose de l'envoyer par la messagerie. */}
        <Button type="submit" variant="accent" loading={create.isPending} className="m0-send-btn">
          Envoyer le questionnaire
        </Button>
      </form>
      {create.error ? <ErrorBanner>{errorMessage(create.error)}</ErrorBanner> : null}

      {fresh ? <FreshClientLink {...fresh} projectName={projectName} onDone={() => setFresh(null)} /> : null}

      {invitations.data && invitations.data.length > 0 ? (
        <ul className="m0-invites">
          {invitations.data.map((inv) => (
            <ClientInvitationRow
              key={inv.id}
              projectId={projectId}
              invitation={inv}
              onNewLink={(token) => setFresh({ id: inv.id, link: publicLink(token), name: inv.name, email: inv.email })}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function FreshClientLink({
  link,
  name,
  email,
  projectName,
  onDone,
}: {
  id: string;
  link: string;
  name: string | null;
  email: string | null;
  projectName: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div className="m0-fresh" role="status">
      <p className="m0-note">
        Lien personnel{name ? ` pour ${name}` : ''}. <strong>Il ne sera plus affiché ensuite</strong> :
        envoyez-le maintenant. Valable 30 jours.
      </p>
      <input className="zy-input m0-link" readOnly value={link} aria-label="Lien du questionnaire" onFocus={(e) => e.target.select()} />
      <div className="m0-actions m0-actions-left">
        <a className={buttonClass('primary')} href={mailtoFor(email, name, link, projectName)}>
          Envoyer par e-mail
        </a>
        <Button type="button" variant="ghost" onClick={() => void copy()}>
          {copied ? 'Lien copié' : 'Copier le lien'}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Fermer
        </Button>
      </div>
    </div>
  );
}

function ClientInvitationRow({
  projectId,
  invitation,
  onNewLink,
}: {
  projectId: string;
  invitation: Mesure0Invitation;
  onNewLink: (token: string) => void;
}) {
  const rotate = useRotateInvitation(projectId);
  const revoke = useRevokeInvitation(projectId);
  const questions = useMesure0Questions();
  const [open, setOpen] = useState(false);
  const answered = Object.keys(invitation.answers).length;

  return (
    <li className="m0-invite">
      <div className="m0-invite-head">
        <div>
          <p className="m0-invite-name">{invitation.name || invitation.email || 'Répondant sans nom'}</p>
          {invitation.email && invitation.name ? <p className="zy-texte-muet">{invitation.email}</p> : null}
        </div>
        <span className={`m0-status m0-status-${invitation.status}`}>{STATUS_LABELS[invitation.status]}</span>
      </div>
      <div className="m0-actions m0-actions-left">
        {answered > 0 ? (
          <Button type="button" variant="ghost" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {open ? 'Masquer' : `Voir les réponses (${answered})`}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          loading={rotate.isPending}
          onClick={() => rotate.mutate(invitation.id, { onSuccess: (inv) => onNewLink(inv.token) })}
        >
          Nouveau lien
        </Button>
        {invitation.status === 'pending' || invitation.status === 'answered' ? (
          <Button type="button" variant="ghost" loading={revoke.isPending} onClick={() => revoke.mutate(invitation.id)}>
            Couper l’accès
          </Button>
        ) : null}
      </div>
      {open ? (
        <dl className="m0-answers">
          {(questions.data ?? []).filter((q) => invitation.answers[q.key]).map((q) => (
            <div key={q.key}>
              <dt>{q.label}</dt>
              <dd>{invitation.answers[q.key]}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {rotate.error || revoke.error ? <ErrorBanner>{errorMessage(rotate.error ?? revoke.error)}</ErrorBanner> : null}
    </li>
  );
}
